import { useCallback, useMemo } from 'react';
import { useCurrentRouteName, useNavigate, useParams } from '../../../../routes';
import {
  getSurveyCurrentPageIndex,
  getSurveyNextNavRoute,
  getSurveyPreviousNavRoute,
} from '../surveyRoutes';

export type SurveyNavigationState = {
  currentPageIndex: number;
  canGoNext: boolean;
  canGoPrevious: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
};

export type SurveyNavigationActions = {
  goToNext: () => void;
  goToPrevious: () => void;
};

export type SurveyNavigation = SurveyNavigationState & SurveyNavigationActions;

/**
 * Hook for managing survey navigation using the surveyRoutes configuration
 * Automatically detects current route and parameters from the router context
 */
export const useSurveyNavigation = (): SurveyNavigation => {
  const navigate = useNavigate();
  const currentRouteName = useCurrentRouteName();
  const params = useParams();

  // Memoize navigation state to prevent unnecessary recalculations
  const navigationState = useMemo(() => {
    const currentPageIndex = getSurveyCurrentPageIndex(currentRouteName, params ?? {});
    const isValidIndex = currentPageIndex !== -1;

    if (!isValidIndex) {
      return {
        currentPageIndex,
        isFirstStep: false,
        isLastStep: false,
        canGoNext: false,
        canGoPrevious: false,
        nextNavRoute: null,
      };
    }

    const isFirstStep = currentPageIndex === 0;
    const nextNavRoute = getSurveyNextNavRoute(currentPageIndex);
    const isLastStep = nextNavRoute == null || nextNavRoute === '';
    const canGoNext = !isLastStep;
    const canGoPrevious = !isFirstStep;

    return {
      currentPageIndex,
      isFirstStep,
      isLastStep,
      canGoNext,
      canGoPrevious,
      nextNavRoute,
    };
  }, [currentRouteName, params]);

  const { currentPageIndex, isFirstStep, isLastStep, canGoNext, canGoPrevious } = navigationState;

  // Navigate to next step
  const goToNext = useCallback((): void => {
    if (!canGoNext) return;

    const nextRoute = navigationState.nextNavRoute;
    if (nextRoute != null && nextRoute.length > 0) {
      navigate(nextRoute);
    }
  }, [canGoNext, navigationState.nextNavRoute, navigate]);

  // Navigate to previous step
  const goToPrevious = useCallback((): void => {
    if (!canGoPrevious) return;

    const previousRoute = getSurveyPreviousNavRoute(currentPageIndex);
    if (previousRoute != null && previousRoute.length > 0) {
      navigate(previousRoute);
    }
  }, [canGoPrevious, currentPageIndex, navigate]);

  return {
    // State
    currentPageIndex,
    canGoNext,
    canGoPrevious,
    isFirstStep,
    isLastStep,

    // Actions
    goToNext,
    goToPrevious,
  };
};
